import React, { useEffect, useRef, useState } from 'react';
import { Alert, Autocomplete, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { apiFetch } from '../utils/apiFetch';
import { getCatalogStorageSession, isCatalogStorageSessionCurrent } from '../utils/catalogSessionStorage';
import { chooseAutomaticCheckoutStreetSuggestion } from '../utils/checkoutAddressInput';
import GoogleCheckoutFallback from './GoogleCheckoutFallback';

const optionKey = (kind, item) => `${kind}:${JSON.stringify([item.id, item.name, item.address, item.position, item.label])}`;
const named = value => typeof value === 'string' && Boolean(value.trim());
const typedOptions = (kind, items) => {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).filter(item => item && (kind === 'street'
    ? named(item.address?.street) : named(item.id) && named(item.name) && named(item.selectionToken)
      && item.address && typeof item.address === 'object' && !Array.isArray(item.address)
      && typeof item.position?.lat === 'number' && Number.isFinite(item.position.lat) && Math.abs(item.position.lat) <= 90
      && typeof item.position?.lng === 'number' && Number.isFinite(item.position.lng) && Math.abs(item.position.lng) <= 180))
    .map(item => ({ kind, item, key: optionKey(kind, item) }))
    .filter(option => { if (seen.has(option.key)) return false; seen.add(option.key); return true; });
};

// Search text has its own state: selecting a place with no source street keeps
// its name visible here while the geographic draft retains the source fields.
export default function CheckoutAddressSearch({ apiUrl, city, state, contextKey, street = '', placeName = '',
  disabled = false, required = false, legacyStreet = false, googleDraftKey = '', onGoogleSelected,
  number = '', ownLocationEstimated = false, hasResolvedLocation = false, resolvingLocation = false,
  onGoogleSearchActiveChange, onInput, onStreetSelect, onPlaceSelect, onEditingChange }) {
  const [query, setQuery] = useState(placeName || street);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [searched, setSearched] = useState(false);
  const [focused, setFocused] = useState(false);
  const [googleSearching, setGoogleSearching] = useState(false);
  const generation = useRef(0);
  const optionOwner = useRef(null);
  const automaticKey = useRef('');
  const currentContext = useRef('');
  const callbacks = useRef({ onInput, onStreetSelect, onPlaceSelect, onEditingChange });
  callbacks.current = { onInput, onStreetSelect, onPlaceSelect, onEditingChange };
  const context = JSON.stringify([contextKey, city, state, disabled, legacyStreet]);
  if (currentContext.current !== context) { currentContext.current = context; generation.current += 1; }
  useEffect(() => {
    generation.current += 1; optionOwner.current = null; automaticKey.current = '';
    setQuery(placeName || street); setOptions([]); setMessage(''); setLoading(false); setSearched(false);
  }, [contextKey, city, state, street, placeName]);

  useEffect(() => {
    const sequence = ++generation.current;
    const session = getCatalogStorageSession();
    const ownerContext = context;
    const abort = new AbortController();
    let active = true;
    const current = () => active && sequence === generation.current && ownerContext === currentContext.current
      && isCatalogStorageSessionCurrent(session) && !abort.signal.aborted;
    if (googleSearching) { setOptions([]); setLoading(false); optionOwner.current = null; return undefined; }
    setOptions([]); setLoading(false); setMessage(''); setSearched(false); optionOwner.current = null;
    if (disabled || !focused || placeName || query.trim().length < 3 || !String(city || '').trim()) return undefined;
    const channels = { street: null, place: null };
    const publish = () => {
      if (!current()) return;
      const previous = optionOwner.current?.sequence === sequence ? optionOwner.current.options : [];
      const nextOptions = [...typedOptions('street', channels.street?.suggestions), ...typedOptions('place', channels.place?.suggestions)]
        .map(option => previous.find(loaded => loaded.key === option.key && loaded.item === option.item) || option);
      optionOwner.current = { sequence, context: ownerContext, session, options: nextOptions };
      setOptions(nextOptions);
    };
    const search = async kind => {
      const endpoint = kind === 'place' ? 'place-suggestions' : legacyStreet ? 'address-suggestions' : 'destination-suggestions';
      const response = await apiFetch(`${apiUrl}/api/delivery-pricing/${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: abort.signal,
        body: JSON.stringify({ q: query.trim().slice(0, 120), city, state }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Não foi possível completar a busca agora.');
      if (!Array.isArray(data.suggestions)) throw new Error('Não foi possível completar a busca agora.');
      channels[kind] = data; publish();
      return data;
    };
    const timer = window.setTimeout(async () => {
      setLoading(true);
      const deadline = window.setTimeout(() => abort.abort(), 10000);
      try {
        const results = await Promise.all(['street', 'place'].map(kind => search(kind).then(
          value => ({ status: 'fulfilled', value }), reason => ({ status: 'rejected', reason }))));
        // An aborted/current-session failure must not authorize auto-adoption.
        if (!active || sequence !== generation.current || ownerContext !== currentContext.current
          || !isCatalogStorageSessionCurrent(session)) return;
        const success = results.every(result => result.status === 'fulfilled') && !abort.signal.aborted;
        setSearched(true);
        if (!success) setMessage('A busca não pôde ser completada. Escolha uma opção disponível ou preencha o endereço.');
        if (success && channels.place.suggestions.length === 0
          && typedOptions('street', channels.street.suggestions).length === channels.street.suggestions.length) {
          const automatic = chooseAutomaticCheckoutStreetSuggestion({ typedStreet: query,
            items: channels.street.suggestions, searchComplete: channels.street.suggestionsComplete === true,
            appliedKey: automaticKey.current });
          if (automatic && current()) {
            automaticKey.current = automatic.key;
            generation.current += 1; abort.abort(); optionOwner.current = null;
            setOptions([]); setQuery(automatic.suggestion.address.street); setFocused(false);
            callbacks.current.onEditingChange?.(false);
            callbacks.current.onStreetSelect(automatic.suggestion);
          }
        }
      } finally {
        window.clearTimeout(deadline);
        if (active && sequence === generation.current) setLoading(false);
      }
    }, 350);
    return () => { active = false; window.clearTimeout(timer); abort.abort(); };
  }, [apiUrl, query, city, state, context, focused, disabled, legacyStreet, placeName, googleSearching]);

  return <Stack spacing={1}>
    <Autocomplete freeSolo value={null} disabled={disabled} inputValue={query} options={options}
      clearText="Limpar" closeText="Fechar" openText="Abrir" filterOptions={items => items} loading={loading}
      getOptionLabel={option => typeof option === 'string' ? option : option.kind === 'place'
        ? option.item.name : option.item.address.street}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      onInputChange={(_, value, reason) => {
        if (!['input', 'clear'].includes(reason)) return;
        generation.current += 1; optionOwner.current = null; automaticKey.current = '';
        setOptions([]); setLoading(false); setSearched(false); setMessage(''); setQuery(value); setFocused(true);
        callbacks.current.onEditingChange?.(true); callbacks.current.onInput(value);
      }}
      onChange={(_, option) => {
        if (!option || typeof option === 'string') return;
        const owner = optionOwner.current;
        if (!owner || owner.sequence !== generation.current || owner.context !== currentContext.current
          || !owner.options.includes(option) || !isCatalogStorageSessionCurrent(owner.session)) return;
        generation.current += 1; optionOwner.current = null; setOptions([]); setLoading(false); setFocused(false);
        setQuery(option.kind === 'place' ? option.item.name : option.item.address.street);
        callbacks.current.onEditingChange?.(false);
        if (option.kind === 'place') callbacks.current.onPlaceSelect(option.item);
        else callbacks.current.onStreetSelect(option.item);
      }}
      noOptionsText="Nenhuma rua ou local encontrado. Você pode preencher o endereço."
      renderOption={(props, option) => <li {...props} key={option.key}><Stack>
        <Typography sx={{ overflowWrap: 'anywhere' }}>{option.kind === 'place' ? option.item.name : option.item.address.street}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {option.kind === 'place' ? 'Local' : 'Rua'} · {option.item.label}
        </Typography>
      </Stack></li>}
      renderInput={params => <TextField {...params} label="Rua" required={required}
        onFocus={() => { setFocused(true); callbacks.current.onEditingChange?.(true); }}
        onBlur={() => { callbacks.current.onEditingChange?.(false); }}
        placeholder="Ex.: Rua Paraná, Havan ou Praça Wilson Jofre"
        helperText={city ? 'Digite ao menos 3 caracteres do nome da rua ou de um local e selecione a opção correta.' : 'Informe a cidade abaixo para buscar ruas e locais.'}
        inputProps={{ ...params.inputProps, maxLength: 120, autoComplete: 'off' }}
        InputProps={{ ...params.InputProps, endAdornment: <>{loading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</> }} />}
    />
    {searched && !loading && !message && options.length === 0 && query.trim().length >= 3 && !placeName
      && <Typography variant="body2" color="text.secondary">Nenhuma rua ou local encontrado nesta cidade. Você pode preencher o endereço.</Typography>}
    {message && <Alert severity="info">{message}</Alert>}
    {onGoogleSelected && <GoogleCheckoutFallback apiUrl={apiUrl}
      eligible={(googleSearching || (!loading && !resolvingLocation && (searched || ownLocationEstimated)))
        && !placeName && (!hasResolvedLocation || ownLocationEstimated)}
      query={query} street={street} number={number} city={city} state={state} draftKey={googleDraftKey} contextKey={contextKey}
      disabled={disabled} onSelected={onGoogleSelected} onSearchActiveChange={active => {
        if (active) {
          generation.current += 1; optionOwner.current = null;
          setOptions([]); setLoading(false); setFocused(false); callbacks.current.onEditingChange?.(false);
        }
        setGoogleSearching(active); onGoogleSearchActiveChange?.(active);
      }} />}
  </Stack>;
}
